/**
 * Ficha de una oportunidad: alta, edición, ganar / perder / reabrir y borrar —
 * `OD-0048` D-01..D-07.
 *
 * - Una sola pantalla. El cliente se busca entre los que ya hay o, si el nombre
 *   escrito no es de ninguno, se crea AL GUARDAR solo con el nombre: un cliente
 *   provisional sin NIF (`URS §723`), por la ruta normal de clientes. No hay que
 *   elegir ninguna opcion de "crear": escribir el nombre basta.
 * - El importe estimado se escribe y se envía como TEXTO; la web no lo convierte
 *   a número ni lo suma.
 * - Al ganarla se pregunta [Crear presupuesto] o [No crear documento] (`D-03`).
 *   Crear presupuesto ABRE el formulario con el cliente puesto: no se guarda
 *   nada hasta que el usuario lo guarda. Y nunca hay una factura de por medio.
 * - Siete etapas (`OD-0048-ENMIENDA-01`). Las cinco abiertas se eligen con un
 *   toque y se puede saltar; con un presupuesto ENLAZADO, las de presupuesto las
 *   marca el presupuesto y aquí solo se enseñan.
 * - Cualificación (`OD-0058`): cinco preguntas de sí / no / no lo sé, un toque
 *   cada una, y el porcentaje que CALCULA la API. Se puede fijar a mano y
 *   volver al cálculo. Responder no cambia la etapa: con las cinco respondidas
 *   en «Identificada», se ofrece pasar a «Cualificada».
 * - Y desde su ENMIENDA-01 las hace José de Faktelio, de una en una, con
 *   consejos y un siguiente paso que se apunta como tarea (`./Jose.tsx`).
 * - Tras la prueba del propietario en staging (2026-10-25): antes de crear un
 *   cliente nuevo se enseñan los PARECIDOS y hay que confirmar que es otro;
 *   ganar o perder se confirma; cerrada, la ficha es de solo lectura hasta
 *   «Reabrir», y al cerrarla se ofrece dar por terminadas sus tareas.
 * - `OD-0058-ENMIENDA-02` G-08: al crearla, o al moverla de etapa sin
 *   cualificar, José invita a responder y la ficha baja hasta él. La propuesta
 *   de «Cualificada» y el «Volver al cálculo» los pinta José.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  ESTADO_VALIDACION,
  type Cliente,
  type DatosOportunidad,
  type ErrorApi,
  ETAPAS_ABIERTAS,
  type EtapaOportunidad,
  MOTIVOS_PERDIDA,
  type MotivoPerdida,
  type Oportunidad,
  type PreguntaCualificacion,
  type RespuestaCualificacion,
} from "../api/cliente";
import { t } from "../i18n/es";
import { AreaTexto, Aviso, Boton, Buscador, Campo, Tarjeta } from "../ui/primitivas";
import { BloqueActividades } from "./Actividades";
import { clientesParecidos, mismoCliente } from "../ui/clientes-parecidos";
import { clavePaso, estadoCualificacion, fechaEnDias, type Paso } from "../ui/jose";
import { ConversacionJose, textoPaso } from "./Jose";
import { SoloBusiness } from "./PantallaOportunidades";

type Formulario = {
  readonly title: string;
  readonly estimatedAmount: string;
  readonly expectedCloseDate: string;
  readonly notes: string;
};

const VACIO: Formulario = { title: "", estimatedAmount: "", expectedCloseDate: "", notes: "" };

function formularioDe(o: Oportunidad): Formulario {
  return {
    title: o.title,
    estimatedAmount: o.estimatedAmount ?? "",
    expectedCloseDate: o.expectedCloseDate ?? "",
    notes: o.notes ?? "",
  };
}

const vacioANulo = (v: string): string | null => (v.trim() === "" ? null : v.trim());

export function PantallaOportunidad({
  id,
  clienteInicial = null,
  invitar = null,
  hayConexion,
  onVolver,
  onAbrir,
  onCrearPresupuesto,
  onAbrirPresupuesto,
  onVerPlan,
  onSesionCaducada,
}: {
  /** `null` = alta. */
  id: string | null;
  /** Desde la ficha de un cliente: el alta llega con él puesto. */
  clienteInicial?: string | null;
  /** G-08: llega recién creada, o movida en el tablero sin cualificar. */
  invitar?: "creada" | "movida" | null;
  hayConexion: boolean;
  onVolver: () => void;
  /** `recienCreada`: la ficha que se abre invita a cualificar. */
  onAbrir: (id: string, recienCreada?: boolean) => void;
  /**
   * D-03 y D-E2: abre un presupuesto NUEVO con el cliente y el concepto puestos.
   * No crea nada; al guardarlo, el presupuesto queda enlazado a la oportunidad.
   */
  onCrearPresupuesto: (clienteId: string, concepto: string, oportunidadId: string) => void;
  onAbrirPresupuesto: (quoteId: string) => void;
  onVerPlan: () => void;
  onSesionCaducada: () => void;
}) {
  const [oportunidad, setOportunidad] = useState<Oportunidad | null>(null);
  const [datos, setDatos] = useState<Formulario>(VACIO);
  const [clientes, setClientes] = useState<readonly Cliente[]>([]);
  const [clienteId, setClienteId] = useState<string>(clienteInicial ?? "");
  const [busquedaCliente, setBusquedaCliente] = useState("");
  const [cargando, setCargando] = useState(true);
  const [soloBusiness, setSoloBusiness] = useState(false);
  const [errores, setErrores] = useState<Readonly<Record<string, string>>>({});
  const [mensaje, setMensaje] = useState<{ tono: "exito" | "error" | "aviso" | "info"; texto: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [confirmandoBorrado, setConfirmandoBorrado] = useState(false);
  const [recienGanada, setRecienGanada] = useState(false);
  // Ganar o perder se confirma: un toque en falso cerraba la oportunidad.
  const [confirmandoCierre, setConfirmandoCierre] = useState<"WON" | "LOST" | null>(null);
  // Recién cerrada, el bloque de tareas ofrece dar por terminadas las pendientes.
  const [recienCerrada, setRecienCerrada] = useState<"WON" | "LOST" | null>(null);
  // OD-0048-ENMIENDA-02 F-01: el motivo al perderla, opcional, y su nota con «Otro».
  const [motivo, setMotivo] = useState<MotivoPerdida | null>(null);
  const [notaMotivo, setNotaMotivo] = useState("");
  // F-04: otra ganada del mismo cliente, para que José proponga «Sí» en «¿Repite?».
  const [historial, setHistorial] = useState<{ readonly ganada: string } | null>(null);
  // Hay clientes parecidos y el usuario dice que el suyo es otro: se crea.
  const [nuevoConfirmado, setNuevoConfirmado] = useState(false);
  // OD-0058: el porcentaje que se escribe a mano, mientras se escribe.
  const [ajuste, setAjuste] = useState<string | null>(null);
  const [errorAjuste, setErrorAjuste] = useState<string | null>(null);
  // El aviso de un fallo al cualificar se enseña en su tarjeta, no en la del formulario.
  const [avisoEnCualificacion, setAvisoEnCualificacion] = useState(false);
  // G-08: lo que dice José al crearla o al moverla sin cualificar.
  const [invitacion, setInvitacion] = useState<string | null>(null);
  // E-05: al apuntar el paso de José, el bloque de tareas se vuelve a cargar.
  const [versionTareas, setVersionTareas] = useState(0);
  // Una clave por formulario: si el alta del cliente provisional se reintenta,
  // no crea dos (TDS-000 §5.1).
  const [claveCliente] = useState(() => crypto.randomUUID());

  useEffect(() => {
    void (async () => {
      const [cli, opo] = await Promise.all([api.listarClientes("ACTIVE"), id === null ? Promise.resolve(null) : api.obtenerOportunidad(id)]);
      setCargando(false);
      const lista = cli.ok ? cli.datos : [];
      setClientes(lista);
      if (!cli.ok && cli.estado === 401) return onSesionCaducada();
      if (opo === null) {
        const suyo = lista.find((c) => c.id === clienteInicial);
        if (suyo !== undefined) setBusquedaCliente(suyo.displayName);
        // Comprobar el plan ANTES de que rellene nada: fuera de Business no se
        // puede guardar, y es mejor decirlo antes que despues de escribir.
        const plan = await api.listarOportunidades("OPEN");
        if (!plan.ok && plan.estado === 402) setSoloBusiness(true);
        return;
      }
      if (opo.ok) {
        setOportunidad(opo.datos.oportunidad);
        if (invitar !== null && estadoCualificacion(opo.datos.oportunidad) !== null) invitarACualificar(opo.datos.oportunidad, invitar);
        setHistorial(opo.datos.historial);
        setDatos(formularioDe(opo.datos.oportunidad));
        setClienteId(opo.datos.oportunidad.customer?.id ?? "");
        setBusquedaCliente(opo.datos.oportunidad.customer?.displayName ?? "");
      } else if (opo.estado === 401) onSesionCaducada();
      else if (opo.estado === 402) setSoloBusiness(true);
      else setMensaje({ tono: "error", texto: opo.estado === 404 ? t.oportunidad.noEncontrada : t.comun.errorGenerico });
    })();
    // `invitar` solo cuenta al abrirla: el Panel la monta de nuevo con cada id.
  }, [id, clienteInicial, onSesionCaducada]);

  /** G-08: José lo dice y la ficha baja hasta él, cuando ya está pintado. */
  function invitarACualificar(o: Oportunidad, como: "creada" | "movida"): void {
    setInvitacion(como === "creada" ? t.jose.invitacion.creada : t.jose.invitacion.movida(t.oportunidades.etapas[o.stage]));
    window.setTimeout(() => {
      const jose = document.getElementById("cualificacion-jose");
      // La tarjeta entera, con su título: no solo la conversación.
      (jose?.closest("section") ?? jose)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  }

  // En el movil la barra de etapas se desplaza de lado: la actual tiene que
  // quedar a la vista sin que nadie la busque.
  const barra = useRef<HTMLOListElement | null>(null);
  useEffect(() => {
    const actual = barra.current?.querySelector<HTMLElement>('[aria-current="step"]');
    if (barra.current !== null && actual !== null && actual !== undefined) {
      barra.current.scrollLeft = actual.offsetLeft - (barra.current.clientWidth - actual.offsetWidth) / 2;
    }
  }, [oportunidad?.stage, oportunidad?.openStage, cargando]);

  const nombreEscrito = busquedaCliente.trim().replace(/\s+/g, " ");
  const opcionesCliente = useMemo(() => clientes.map((c) => ({ id: c.id, texto: c.displayName })), [clientes]);
  // Sin mayusculas ni tildes de por medio: "marta ruiz" es la Marta Ruíz que ya
  // existe, no una segunda.
  const mismoNombre = (c: Cliente): boolean => mismoCliente(nombreEscrito, c.displayName);
  const clienteNuevo = clienteId === "" && nombreEscrito !== "" && !clientes.some(mismoNombre);
  // «2018» no es «2018 Apoyo, S.L.», pero se le parece: se pregunta antes de crear otro.
  const parecidos = useMemo(() => (clienteNuevo ? clientesParecidos(nombreEscrito, clientes) : []), [clienteNuevo, nombreEscrito, clientes]);
  const cerrada = oportunidad !== null && oportunidad.status !== "OPEN";
  const fechaPasada = !cerrada && datos.expectedCloseDate !== "" && datos.expectedCloseDate < fechaEnDias(new Date(), 0);

  const errorDe = (campo: string): string | null =>
    errores[campo] === undefined ? null : (t.oportunidad.errores[errores[campo]] ?? t.comun.errorGenerico);

  /** Al corregir un campo, su error se va: no se queda hasta el siguiente «Guardar». */
  function quitarError(campo: string): void {
    setErrores((e) => {
      if (e[campo] === undefined) return e;
      const resto = { ...e };
      delete resto[campo];
      return resto;
    });
  }

  function elegirCliente(c: Cliente): void {
    setClienteId(c.id);
    setBusquedaCliente(c.displayName);
    quitarError("customerId");
  }

  function rechazo(r: { estado: number; error: ErrorApi | null }): void {
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === 402) return setSoloBusiness(true);
    if (r.estado === 403) return setMensaje({ tono: "aviso", texto: t.oportunidades.sinPermiso });
    if (r.estado === ESTADO_VALIDACION && r.error !== null) return setErrores(r.error.campos);
    if (r.estado === 409 && r.error?.motivo === "INVALID_TRANSITION") return setMensaje({ tono: "aviso", texto: t.oportunidad.transicionNoPermitida });
    if (r.estado === 409 && r.error?.motivo === "QUOTE_DRIVES_STAGE") return setMensaje({ tono: "aviso", texto: t.oportunidad.etapaDelPresupuesto });
    setMensaje({ tono: "error", texto: r.estado === 0 ? t.comun.sinRed : t.comun.errorGenerico });
  }

  function limpiarAvisos(): void {
    setMensaje(null);
    setErrores({});
    setRecienGanada(false);
    setAvisoEnCualificacion(false);
  }

  async function guardar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    limpiarAvisos();
    let cliente = clienteId === "" ? (clientes.find(mismoNombre)?.id ?? "") : clienteId;
    // Todo lo que falta, de una vez, y ANTES de crear el cliente: si no, un
    // título vacío dejaba un cliente creado y la oportunidad sin guardar.
    const faltan: Record<string, string> = {};
    if (datos.title.trim() === "") faltan.title = "REQUIRED";
    if (cliente === "" && nombreEscrito === "") faltan.customerId = "REQUIRED";
    else if (cliente === "" && parecidos.length > 0 && !nuevoConfirmado) faltan.customerId = "SIMILAR";
    if (Object.keys(faltan).length > 0) {
      setErrores(faltan);
      return;
    }
    setOcupado(true);
    if (cliente === "") {
      const c = await api.crearClienteProvisional(nombreEscrito, claveCliente);
      if (!c.ok) {
        setOcupado(false);
        if (c.estado === ESTADO_VALIDACION) return setErrores({ customerId: "REQUIRED" });
        // 429: sin plazas de cliente. El mensaje de la API ya dice qué hacer (URS §110).
        if (c.estado === 429 && c.error !== null) return setMensaje({ tono: "aviso", texto: `${c.error.message} ${c.error.action ?? ""}`.trim() });
        return rechazo(c);
      }
      cliente = c.datos.id;
      setClientes((l) => [...l, c.datos]);
      setClienteId(cliente);
      setBusquedaCliente(c.datos.displayName);
    }
    const cuerpo: DatosOportunidad = {
      customerId: cliente,
      title: datos.title,
      estimatedAmount: vacioANulo(datos.estimatedAmount),
      expectedCloseDate: vacioANulo(datos.expectedCloseDate),
      notes: vacioANulo(datos.notes),
    };
    const r = oportunidad === null ? await api.crearOportunidad(cuerpo) : await api.editarOportunidad(oportunidad.id, cuerpo);
    setOcupado(false);
    if (!r.ok) return rechazo(r);
    if (oportunidad === null) return onAbrir(r.datos.oportunidad.id, true);
    setOportunidad(r.datos.oportunidad);
    setDatos(formularioDe(r.datos.oportunidad));
    setMensaje({ tono: "exito", texto: t.oportunidad.guardada });
  }

  async function cambiarEtapa(etapa: EtapaOportunidad): Promise<void> {
    if (oportunidad === null) return;
    limpiarAvisos();
    setOcupado(true);
    // Sin motivo marcado también se cierra: la pregunta es opcional (F-01).
    const conMotivo = etapa === "LOST" ? { lostReason: motivo, lostNote: motivo === "OTHER" ? vacioANulo(notaMotivo) : null } : null;
    const r = await api.cambiarEtapaOportunidad(oportunidad.id, etapa, conMotivo);
    setOcupado(false);
    // Si la API rechaza la nota, el paso del motivo sigue abierto con su error.
    if (!r.ok) return rechazo(r);
    setConfirmandoCierre(null);
    setMotivo(null);
    setNotaMotivo("");
    setOportunidad(r.datos.oportunidad);
    const cierre = etapa === "WON" || etapa === "LOST" ? etapa : null;
    setRecienCerrada(cierre);
    if (cierre !== null) setVersionTareas((v) => v + 1);
    // Solo se pregunta si la API ofrece crear el presupuesto: con uno ya
    // enlazado no lo ofrece.
    setRecienGanada(etapa === "WON" && r.datos.ofertas.includes("CREATE_QUOTE"));
    // G-08: movida a otra abierta sin cualificar, José invita a hacerlo.
    if (cierre === null && estadoCualificacion(r.datos.oportunidad) !== null) invitarACualificar(r.datos.oportunidad, "movida");
    else setInvitacion(null);
  }

  /** OD-0058: una respuesta por toque, o el ajuste a mano. La API recalcula. */
  async function cualificar(cambio: Partial<Record<PreguntaCualificacion, RespuestaCualificacion | null>> & { probabilityOverride?: string | null }): Promise<boolean> {
    if (oportunidad === null) return false;
    limpiarAvisos();
    setOcupado(true);
    const r = await api.cualificarOportunidad(oportunidad.id, cambio);
    setOcupado(false);
    if (!r.ok) {
      // El porcentaje mal escrito se dice junto al campo; el resto, en la tarjeta.
      if (r.estado === ESTADO_VALIDACION && r.error?.campos.probabilityOverride !== undefined) {
        setErrorAjuste(t.oportunidad.cualificacion.ajusteInvalido);
        return false;
      }
      setAvisoEnCualificacion(true);
      rechazo(r);
      return false;
    }
    setOportunidad(r.datos.oportunidad);
    // Ya cualificada, la invitación sobra.
    if (estadoCualificacion(r.datos.oportunidad) === null) setInvitacion(null);
    return true;
  }

  /**
   * OD-0058-ENMIENDA-01 E-05: el paso de José, apuntado como tarea de esta
   * oportunidad (y de su cliente) por la ruta normal de tareas. Se puede
   * editar o borrar después como cualquier otra.
   */
  async function apuntarPaso(paso: Paso): Promise<boolean> {
    if (oportunidad === null) return false;
    limpiarAvisos();
    const r = await api.crearActividad({
      kind: paso.kind,
      title: textoPaso(paso).tarea,
      notes: t.jose.notaTarea,
      dueDate: fechaEnDias(new Date(), paso.dias),
      dueTime: null,
      important: false,
      customerId: oportunidad.customer?.id ?? null,
      opportunityId: oportunidad.id,
      quoteId: null,
      invoiceId: null,
      repeat: null,
    }, false, clavePaso(paso.clave));
    if (!r.ok) {
      setAvisoEnCualificacion(true);
      rechazo(r);
      return false;
    }
    setVersionTareas((v) => v + 1);
    return true;
  }

  /** El texto va tal cual: la API comprueba que es un entero de 0 a 100. */
  async function guardarAjuste(): Promise<void> {
    const v = (ajuste ?? "").trim();
    if (!/^\d{1,3}$/.test(v)) return setErrorAjuste(t.oportunidad.cualificacion.ajusteInvalido);
    setErrorAjuste(null);
    if (await cualificar({ probabilityOverride: v })) setAjuste(null);
  }

  async function eliminar(): Promise<void> {
    if (oportunidad === null) return;
    limpiarAvisos();
    setOcupado(true);
    const r = await api.eliminarOportunidad(oportunidad.id);
    setOcupado(false);
    if (!r.ok) return rechazo(r);
    onVolver();
  }

  const titulo = oportunidad === null ? t.oportunidad.nueva : oportunidad.title;

  if (soloBusiness) {
    return (
      <>
        <Boton variante="secundario" onClick={onVolver}>
          {t.oportunidad.volver}
        </Boton>
        <Tarjeta titulo={t.oportunidades.titulo}>
          <SoloBusiness onVerPlan={onVerPlan} />
        </Tarjeta>
      </>
    );
  }

  if (cargando) {
    return (
      <Tarjeta titulo={titulo}>
        <p>{t.oportunidades.cargando}</p>
      </Tarjeta>
    );
  }

  const sinPermisoDeEscritura = !hayConexion || ocupado;

  return (
    <>
      <Boton variante="secundario" onClick={onVolver}>
        {t.oportunidad.volver}
      </Boton>

      {recienGanada && oportunidad !== null && oportunidad.customer !== null ? (
        <Tarjeta titulo={t.oportunidad.ganadaTitulo}>
          <p className="ayuda-neutra">{t.oportunidad.ganadaDetalle}</p>
          <div className="acciones">
            <Boton onClick={() => onCrearPresupuesto(oportunidad.customer?.id ?? "", oportunidad.title, oportunidad.id)}>
              {t.oportunidad.crearPresupuesto}
            </Boton>
            <Boton variante="secundario" onClick={() => setRecienGanada(false)}>
              {t.oportunidad.noCrearDocumento}
            </Boton>
          </div>
        </Tarjeta>
      ) : null}

      {/* La etapa va ARRIBA, como en un CRM: es lo primero que se mira. */}
      {oportunidad !== null ? (
        <Tarjeta titulo={`${t.oportunidad.etapa}: ${t.oportunidades.etapas[oportunidad.stage]}`}>
          {/*
            LA BARRA DE ETAPAS, como el pipeline de un CRM: las cinco abiertas de
            izquierda a derecha, las ya pasadas rellenas, la actual marcada y las
            que faltan en blanco. Un toque en una la lleva ahí (D-E3). Cerrada, la
            barra se queda donde se cerró y encima va la insignia de ganada o
            perdida.
          */}
          {(() => {
            const actual = oportunidad.status === "OPEN" ? oportunidad.stage : oportunidad.openStage;
            const indice = ETAPAS_ABIERTAS.indexOf(actual as (typeof ETAPAS_ABIERTAS)[number]);
            return (
              <ol ref={barra} className={`pipeline${oportunidad.status === "OPEN" ? "" : " pipeline-cerrada"}`} aria-label={t.oportunidad.etapa}>
                {ETAPAS_ABIERTAS.map((e, i) => (
                  <li key={e}>
                    <button
                      type="button"
                      className={`pipeline-paso pipeline-paso-${i < indice ? "hecho" : i === indice ? "actual" : "pendiente"}`}
                      aria-current={i === indice ? "step" : undefined}
                      // Cerrada, o con presupuesto enlazado (D-E2), la barra solo informa.
                      disabled={sinPermisoDeEscritura || oportunidad.status !== "OPEN" || oportunidad.quote !== null}
                      onClick={() => void cambiarEtapa(e)}
                    >
                      {i < indice ? (
                        <span className="pipeline-check" aria-hidden="true">
                          ✓
                        </span>
                      ) : null}
                      <span className="pipeline-nombre">{t.oportunidades.etapas[e]}</span>
                      {i < indice ? <span className="solo-lector"> {t.oportunidad.etapaHecha}</span> : null}
                    </button>
                  </li>
                ))}
              </ol>
            );
          })()}
          {oportunidad.status !== "OPEN" ? (
            <p>
              <span className={`insignia crm-cierre crm-cierre-${oportunidad.status}`}>{t.oportunidades.etapas[oportunidad.stage]}</span>
            </p>
          ) : null}
          {oportunidad.status === "LOST" && oportunidad.lostReason !== null ? (
            <p className="crm-motivo">{t.oportunidad.motivoDePerdida(t.oportunidad.motivos[oportunidad.lostReason], oportunidad.lostNote)}</p>
          ) : null}
          {oportunidad.status === "OPEN" ? (
            <>
              {oportunidad.quote !== null ? <p className="ayuda-neutra">{t.oportunidad.etapaDelPresupuesto}</p> : null}
              {confirmandoCierre === null ? (
                <div className="acciones">
                  <Boton onClick={() => setConfirmandoCierre("WON")} deshabilitado={sinPermisoDeEscritura}>
                    {t.oportunidad.ganada}
                  </Boton>
                  <Boton variante="secundario" onClick={() => setConfirmandoCierre("LOST")} deshabilitado={sinPermisoDeEscritura}>
                    {t.oportunidad.perdida}
                  </Boton>
                </div>
              ) : (
                <>
                  <Aviso tono="aviso">{t.oportunidad.confirmarCierre[confirmandoCierre]}</Aviso>
                  {confirmandoCierre === "LOST" ? (
                    <div className="crm-motivo-pregunta">
                      <p className="jose-pregunta" id="opo-motivo">
                        {t.oportunidad.porQue}
                      </p>
                      <div className="fichas" role="group" aria-labelledby="opo-motivo">
                        {MOTIVOS_PERDIDA.map((m) => (
                          <button
                            key={m}
                            type="button"
                            className="ficha"
                            aria-pressed={motivo === m}
                            disabled={sinPermisoDeEscritura}
                            // Tocar el marcado lo desmarca: se cierra sin motivo.
                            onClick={() => {
                              setMotivo((x) => (x === m ? null : m));
                              quitarError("lostReason");
                            }}
                          >
                            {t.oportunidad.motivos[m]}
                          </button>
                        ))}
                      </div>
                      {motivo === "OTHER" ? (
                        <Campo
                          id="opo-motivo-nota"
                          etiqueta={t.oportunidad.notaMotivo}
                          valor={notaMotivo}
                          onChange={(v) => {
                            setNotaMotivo(v);
                            quitarError("lostNote");
                          }}
                          requerido={false}
                          autoComplete="off"
                          error={errorDe("lostNote")}
                        />
                      ) : null}
                      <p className="ayuda-neutra">{t.oportunidad.motivoOpcional}</p>
                    </div>
                  ) : null}
                  <div className="acciones">
                    <Boton onClick={() => void cambiarEtapa(confirmandoCierre)} deshabilitado={sinPermisoDeEscritura}>
                      {t.oportunidad.siCerrar[confirmandoCierre]}
                    </Boton>
                    <Boton
                      variante="secundario"
                      onClick={() => {
                        setConfirmandoCierre(null);
                        setMotivo(null);
                        setNotaMotivo("");
                      }}
                    >
                      {t.oportunidad.cancelar}
                    </Boton>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <p className="ayuda-neutra">{t.oportunidad.etapaDeCierre(t.oportunidades.etapas[oportunidad.openStage])}</p>
              <Boton variante="secundario" onClick={() => void cambiarEtapa(oportunidad.openStage)} deshabilitado={sinPermisoDeEscritura}>
                {t.oportunidad.reabrir}
              </Boton>
            </>
          )}
        </Tarjeta>
      ) : null}

      {oportunidad !== null ? (
        <Tarjeta titulo={t.jose.titulo}>
          {mensaje !== null && avisoEnCualificacion ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
          <ConversacionJose
            oportunidad={oportunidad}
            historial={historial}
            invitacion={invitacion}
            deshabilitado={sinPermisoDeEscritura}
            onResponder={(p, r) => cualificar({ [p]: r })}
            onApuntar={apuntarPaso}
            onVolverAlCalculo={() => void cualificar({ probabilityOverride: null })}
            onPasarACualificada={() => void cambiarEtapa("QUALIFIED")}
          />

          {/* G-08: volver al cálculo está junto al número, en la burbuja de José. */}
          {oportunidad.status === "OPEN" && ajuste === null ? (
            <div className="acciones">
              <Boton variante="terciario" onClick={() => setAjuste(oportunidad.probability === null ? "" : String(oportunidad.probability))} deshabilitado={sinPermisoDeEscritura}>
                {t.oportunidad.cualificacion.ajustar}
              </Boton>
            </div>
          ) : null}
          {oportunidad.status === "OPEN" && ajuste !== null ? (
            <div className="cual-ajuste">
              <Campo
                id="cual-porcentaje"
                etiqueta={t.oportunidad.cualificacion.porcentaje}
                valor={ajuste}
                onChange={setAjuste}
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                error={errorAjuste}
              />
              <Boton onClick={() => void guardarAjuste()} deshabilitado={sinPermisoDeEscritura}>
                {t.oportunidad.cualificacion.guardarAjuste}
              </Boton>
              <Boton
                variante="secundario"
                onClick={() => {
                  setAjuste(null);
                  setErrorAjuste(null);
                }}
              >
                {t.oportunidad.cualificacion.cancelarAjuste}
              </Boton>
            </div>
          ) : null}
        </Tarjeta>
      ) : null}

      <Tarjeta titulo={titulo}>
        <form onSubmit={guardar} noValidate>
          {cerrada ? <p className="ayuda-neutra">{t.oportunidad.soloLectura}</p> : null}
          {/* Cerrada, se lee pero no se cambia: para cambiarla se reabre (la API dice 409 igual). */}
          <fieldset className="sin-marco" disabled={cerrada}>
            <Campo
              id="opo-titulo"
              etiqueta={t.oportunidad.titulo}
              valor={datos.title}
              onChange={(v) => {
                setDatos((d) => ({ ...d, title: v }));
                quitarError("title");
              }}
              ayuda={t.oportunidad.tituloAyuda}
              autoComplete="off"
              error={errorDe("title")}
            />
            <Buscador
              id="opo-cliente"
              etiqueta={t.oportunidad.cliente}
              texto={busquedaCliente}
              opciones={opcionesCliente}
              marcador={t.oportunidad.eligeCliente}
              ayuda={t.oportunidad.clienteAyuda}
              onTexto={(v) => {
                setBusquedaCliente(v);
                setNuevoConfirmado(false);
                quitarError("customerId");
              }}
              onElegir={(cid) => setClienteId(cid ?? "")}
              error={errorDe("customerId")}
            />
            {!cerrada && parecidos.length > 0 && !nuevoConfirmado ? (
              <div className="clientes-parecidos">
                <p className="ayuda-neutra">{t.oportunidad.parecidos}</p>
                <ul>
                  {parecidos.map((c) => (
                    <li key={c.id}>
                      <Boton variante="secundario" onClick={() => elegirCliente(c)}>
                        {c.displayName}
                      </Boton>
                    </li>
                  ))}
                </ul>
                <Boton
                  variante="terciario"
                  onClick={() => {
                    setNuevoConfirmado(true);
                    quitarError("customerId");
                  }}
                >
                  {t.oportunidad.esNuevo(nombreEscrito)}
                </Boton>
              </div>
            ) : null}
            {!cerrada && clienteNuevo && (parecidos.length === 0 || nuevoConfirmado) ? (
              <p className="ayuda-neutra">{t.oportunidad.seCrearaCliente(nombreEscrito)}</p>
            ) : null}
            <div className="fila-campos">
              <Campo
                id="opo-importe"
                etiqueta={t.oportunidad.importe}
                valor={datos.estimatedAmount}
                onChange={(v) => {
                  setDatos((d) => ({ ...d, estimatedAmount: v }));
                  quitarError("estimatedAmount");
                }}
                ayuda={t.oportunidad.importeAyuda}
                inputMode="text"
                autoComplete="off"
                requerido={false}
                error={errorDe("estimatedAmount")}
              />
              <Campo
                id="opo-fecha"
                etiqueta={t.oportunidad.fecha}
                tipo="date"
                valor={datos.expectedCloseDate}
                onChange={(v) => {
                  setDatos((d) => ({ ...d, expectedCloseDate: v }));
                  quitarError("expectedCloseDate");
                }}
                requerido={false}
                error={errorDe("expectedCloseDate")}
              />
            </div>
            {/* No se impide: puede que se apunte tarde. Solo se avisa. */}
            {fechaPasada ? <Aviso tono="aviso">{t.oportunidad.fechaPasada}</Aviso> : null}
            <AreaTexto
              id="opo-notas"
              etiqueta={t.oportunidad.notas}
              valor={datos.notes}
              onChange={(v) => {
                setDatos((d) => ({ ...d, notes: v }));
                quitarError("notes");
              }}
              requerido={false}
              error={errorDe("notes")}
            />
          </fieldset>

          {mensaje !== null && !avisoEnCualificacion ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
          {!hayConexion ? <Aviso tono="aviso">{t.comun.sinRed}</Aviso> : null}
          {!cerrada ? (
            <Boton tipo="submit" deshabilitado={sinPermisoDeEscritura}>
              {ocupado ? t.oportunidad.guardando : t.oportunidad.guardar}
            </Boton>
          ) : null}
        </form>
      </Tarjeta>

      {oportunidad !== null && oportunidad.quote !== null ? (
        <Tarjeta titulo={t.oportunidad.presupuestoEnlazado}>
          {oportunidad.quote.status !== null ? (
            <p className="ayuda-neutra">{t.oportunidad.estadosPresupuesto[oportunidad.quote.status] ?? oportunidad.quote.status}</p>
          ) : null}
          {oportunidad.status === "OPEN" && ["REJECTED", "EXPIRED", "CANCELLED"].includes(oportunidad.quote.status ?? "") ? (
            <Aviso tono="aviso">
              {t.oportunidad.presupuestoAcabo(t.oportunidad.estadosPresupuesto[oportunidad.quote.status ?? ""] ?? "")}
            </Aviso>
          ) : null}
          <Boton variante="secundario" onClick={() => onAbrirPresupuesto(oportunidad.quote?.id ?? "")}>
            {t.oportunidad.abrirPresupuesto}
          </Boton>
        </Tarjeta>
      ) : null}

      {oportunidad !== null && oportunidad.quote === null && oportunidad.status === "OPEN" && oportunidad.customer !== null ? (
        <Tarjeta titulo={t.oportunidad.hacerPresupuesto}>
          <p className="ayuda-neutra">{t.oportunidad.hacerPresupuestoDetalle}</p>
          <Boton
            variante="secundario"
            onClick={() => onCrearPresupuesto(oportunidad.customer?.id ?? "", oportunidad.title, oportunidad.id)}
            deshabilitado={sinPermisoDeEscritura}
          >
            {t.oportunidad.hacerPresupuesto}
          </Boton>
        </Tarjeta>
      ) : null}

      {/* OD-0050 D-07: lo pendiente con esta oportunidad y su historial. */}
      {oportunidad !== null ? (
        <BloqueActividades
          key={versionTareas}
          oportunidadId={oportunidad.id}
          cerrada={recienCerrada}
          onAbrirPresupuesto={onAbrirPresupuesto}
          onSesionCaducada={onSesionCaducada}
        />
      ) : null}

      {oportunidad !== null ? (
        <Tarjeta titulo={t.oportunidad.eliminarTitulo}>
          {!confirmandoBorrado ? (
            <Boton variante="peligro" onClick={() => setConfirmandoBorrado(true)} deshabilitado={sinPermisoDeEscritura}>
              {t.oportunidad.eliminar}
            </Boton>
          ) : (
            <>
              <Aviso tono="aviso">{t.oportunidad.confirmarEliminar}</Aviso>
              <div className="acciones">
                <Boton variante="peligro" onClick={() => void eliminar()} deshabilitado={sinPermisoDeEscritura}>
                  {t.oportunidad.siEliminar}
                </Boton>
                <Boton variante="secundario" onClick={() => setConfirmandoBorrado(false)}>
                  {t.oportunidad.cancelar}
                </Boton>
              </div>
            </>
          )}
        </Tarjeta>
      ) : null}
    </>
  );
}
